import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { getAuthSession, type AuthSession } from './app/helpers/authSession';

export let restApiName = '';
export let restApiGatewayEndpoint = '';
export let backendEnv = '';

interface ChannelPartnerBackendExports {
  identityPoolId: string;
  userPoolId: string;
  userPoolClientId: string;
  region: string;
  restApiName: string;
  restApiGatewayEndpoint: string;
  env: string;
  mediaBucketName: string;
  placeIndexName: string;
}

/**
 * Apply the authenticated partner scope to every REST request by default.
 * Invocation-level headers are merged after these library headers by Amplify,
 * so an admin can still explicitly select a different partner where supported.
 */
export const buildRestHeaders = (
  session: AuthSession
): Record<string, string> => {
  const idToken = session.tokens?.idToken;
  if (!idToken) {
    return {};
  }

  const partnerId = idToken.payload?.['custom:channelPartnerId'];
  return {
    Authorization: idToken.toString(),
    ...(typeof partnerId === 'string' && partnerId.trim()
      ? { 'X-Partner-Id': partnerId.trim() }
      : {}),
  };
};

export const configureAmplify = async () => {
  try {
    const res = await fetch('/backendExports.json');
    const output = (await res.json()) as ChannelPartnerBackendExports;
    restApiName = output.restApiName;
    restApiGatewayEndpoint = output.restApiGatewayEndpoint;
    backendEnv = output.env;
    const amplifyConfig: ResourcesConfig = {
      API: {
        REST: {
          [restApiName]: {
            endpoint: output.restApiGatewayEndpoint,
            region: output.region,
          },
        },
      },
      Auth: {
        Cognito: {
          userPoolClientId: output.userPoolClientId,
          userPoolId: output.userPoolId,
          identityPoolId: output.identityPoolId,
          allowGuestAccess: false,
          signUpVerificationMethod: 'code',
        },
      },
      Storage: {
        S3: {
          bucket: output.mediaBucketName,
          region: output.region,
        },
      },
      Geo: {
        LocationService: {
          region: output.region,
          searchIndices: {
            items: [output.placeIndexName],
            default: output.placeIndexName,
          },
        },
      },
    };

    Amplify.configure(amplifyConfig, {
      API: {
        REST: {
          headers: async () => {
            const session = await getAuthSession();
            const headers = buildRestHeaders(session);

            if (!headers.Authorization) {
              console.warn(
                'Amplify REST request omitted Authorization header because no ID token is available.'
              );
            }

            return headers;
          },
        },
      },
    });
  } catch (e) {
    console.error('Failed to load backend exports', e);
    throw e;
  }
};

export default configureAmplify;
